// App-wide unread state for internal staff Chats, shared by the app-sidebar nav badge
// (how many chats have unread messages, visible before opening Chats) and by the
// notification layer, which uses the known-internal-thread-id set to route clicks to the
// Chats page and to apply chat-specific notification rules.
//
// Mirrors threads-overview-store: a module-level cache fed by GET /threads/unread-summary
// that refreshes on login/permission changes, on WS message + thread events (debounced),
// and on a slow interval. Marked-read threads update optimistically via the local edit
// helpers; the next summary fetch reconciles.

import { fetchThreadsUnreadSummary } from '@/lib/api/threads';
import {
  isDirectChatKnownDisabled,
  markDirectChatDisabledFromError,
  markDirectChatEnabled,
} from '@/lib/direct-chat-availability';
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from '@/lib/api/token-store';
import { subscribeChatMessages, subscribeUserThreadEvents } from '@/lib/realtime/chat-realtime-service';
import { hasPermission, subscribePermissions } from '@/lib/stores/permissions-store';
import { getMutedChatThreadIds, subscribeUserPreferences } from '@/lib/stores/user-preference-store';

const NETWORK_REFRESH_MS = 60000;
// Message on a KNOWN internal thread: refresh quickly — this is exactly the badge's job.
const WS_KNOWN_DEBOUNCE_MS = 800;
// Message on an UNKNOWN thread (could be a brand-new chat's first message): refresh too,
// but lazily, and only for non-WhatsApp traffic — patient WhatsApp bursts must not make
// this store hammer the summary endpoint.
const WS_UNKNOWN_DEBOUNCE_MS = 2500;

let byThreadId = {}; // threadId -> unread count (>0 only)
let unreadThreadsCount = 0;
// Every internal thread id this session has seen (summary responses + the Chats pane's
// loaded list). Superset of the unread keys; used for notification scope routing.
const internalThreadIds = new Set();

// Every PRIVATE contact-chat thread id this session has seen (metadata.conversationType ===
// "app_direct"). Tracked alongside the internal set, and for the same reason: notification clicks
// have to know which pane owns a thread, and getting it wrong opens a private conversation inside
// the shared patient inbox.
const directThreadIds = new Set();
let snapshotOwnerUserId = '';
let started = false;
let wsDebounceTimer = null;
const listeners = new Set();

function emit() {
  for (const listener of listeners) {
    try {
      listener(unreadThreadsCount);
    } catch {
      /* ignore listener failures */
    }
  }
}

// Muted chats keep their per-row badge but never count toward the sidebar bubble.
function visibleUnreadThreadCount() {
  const muted = getMutedChatThreadIds();
  let count = 0;
  for (const tid of Object.keys(byThreadId)) {
    if (!muted.has(tid)) count += 1;
  }
  return count;
}

function setCounts(nextByThreadId) {
  byThreadId = nextByThreadId;
  unreadThreadsCount = visibleUnreadThreadCount();
  // Emit unconditionally: even with an unchanged bubble count the per-thread numbers may
  // have moved, and subscribers re-read the map.
  emit();
}

// On a profile switch the previous user's counts must drop immediately — the sidebar
// badge renders inside the new user's session before their first fetch resolves.
function dropIfOwnerChanged(currentUserId) {
  if (snapshotOwnerUserId === currentUserId) return;
  snapshotOwnerUserId = currentUserId;
  internalThreadIds.clear();
  if (Object.keys(byThreadId).length > 0 || unreadThreadsCount !== 0) {
    byThreadId = {};
    unreadThreadsCount = 0;
    emit();
  }
}

function chatsEligible() {
  return Boolean(getAuthToken()) && hasPermission('chats', 'view');
}

// Private contact chats have their own pane and their own badge, so their own count. Kept in this
// store rather than a new one because it shares everything that matters — the auth/permission
// lifecycle, the WS debounce and the owner-change guard.
let privateUnreadCount = 0;
const privateListeners = new Set();

function emitPrivate() {
  for (const listener of privateListeners) {
    try {
      listener(privateUnreadCount);
    } catch {
      /* a bad listener must not stop the others */
    }
  }
}

/** Number of private contact chats holding unread messages (0 until the first fetch). */
export function getPrivateUnreadThreadsCount() {
  ensureStarted();
  return privateUnreadCount;
}

export function subscribePrivateUnread(listener) {
  if (typeof listener !== 'function') return () => {};
  ensureStarted();
  privateListeners.add(listener);
  return () => {
    privateListeners.delete(listener);
  };
}

/**
 * Refreshes the private-chat unread count.
 *
 * Deliberately NOT gated on the `chats` permission: a private conversation is personal, and the
 * server gates it on membership alone (requireDirectChatUser). Gating the badge on a clinical
 * module would hide a nurse's own unread messages from her.
 */
export async function refreshPrivateUnread() {
  if (!getAuthToken()) {
    if (privateUnreadCount !== 0) {
      privateUnreadCount = 0;
      emitPrivate();
    }
    return;
  }
  // The server said "contact chat is not enabled" once this session — stop asking. Without this
  // the 60s interval 403'd forever on every server with the switch off (console noise + the
  // global permission toast, for every user, about a page nobody was on).
  if (isDirectChatKnownDisabled()) {
    if (privateUnreadCount !== 0) {
      privateUnreadCount = 0;
      emitPrivate();
    }
    return;
  }
  const requestUserId = String(getAuthUser()?.id || '').trim();
  try {
    // This is a background feature probe, not a user action — its 403 must not toast.
    const { byThreadId: next } = await fetchThreadsUnreadSummary({
      scope: 'direct',
      suppressPermissionDeniedEvent: true,
    });
    markDirectChatEnabled();
    if (String(getAuthUser()?.id || '').trim() !== requestUserId) return;
    for (const tid of Object.keys(next)) {
      directThreadIds.add(tid);
    }
    const count = Object.values(next).filter((n) => Number(n) > 0).length;
    if (count !== privateUnreadCount) {
      privateUnreadCount = count;
      emitPrivate();
    }
  } catch (error) {
    // A 403 latches "feature off" for the session; anything else keeps the previous count and
    // the next tick retries.
    markDirectChatDisabledFromError(error);
  }
}

export async function refreshChatsUnread() {
  if (!chatsEligible()) {
    dropIfOwnerChanged(String(getAuthUser()?.id || '').trim());
    if (Object.keys(byThreadId).length > 0 || unreadThreadsCount !== 0) {
      byThreadId = {};
      unreadThreadsCount = 0;
      emit();
    }
    return;
  }
  const requestUserId = String(getAuthUser()?.id || '').trim();
  dropIfOwnerChanged(requestUserId);
  try {
    const { byThreadId: next } = await fetchThreadsUnreadSummary();
    if (String(getAuthUser()?.id || '').trim() !== requestUserId) return;
    for (const tid of Object.keys(next)) {
      internalThreadIds.add(tid);
    }
    setCounts(next);
  } catch {
    /* keep the previous (same-user) counts; the next tick retries */
  }
}

function scheduleWsRefresh(delayMs) {
  if (wsDebounceTimer) return;
  wsDebounceTimer = setTimeout(() => {
    wsDebounceTimer = null;
    void refreshChatsUnread();
  }, delayMs);
}

/**
 * Patient-channel traffic (the WhatsApp bridge or the Healo mobile app) must never trigger
 * the internal-Chats unknown-thread refresh. WhatsApp messages carry metadata.whatsapp;
 * app messages carry a persisted top-level channel:"app" (stamped by the patient app).
 * Exported for tests.
 */
export function looksLikePatientChannelMessage(message) {
  if (String(message?.channel || '').toLowerCase() === 'app') return true;
  const metadata = message?.metadata;
  if (!metadata || typeof metadata !== 'object') return false;
  const conversationType = String(metadata.conversationType || '').toLowerCase();
  return (
    Boolean(metadata.whatsapp) ||
    conversationType === 'whatsapp' ||
    conversationType === 'app'
  );
}

function ensureStarted() {
  if (started || typeof window === 'undefined') return;
  started = true;
  void refreshChatsUnread();
  void refreshPrivateUnread();
  // Never cleared — the store lives for the app session, like the other module stores.
  setInterval(() => {
    void refreshChatsUnread();
    void refreshPrivateUnread();
  }, NETWORK_REFRESH_MS);
  subscribeAuthStateChange(() => {
    void refreshChatsUnread();
    void refreshPrivateUnread();
  });
  // Chats eligibility can arrive after login (permissions load/refresh) — kick then too.
  subscribePermissions(() => void refreshChatsUnread());
  // Mute toggles change which threads count toward the bubble without new data.
  subscribeUserPreferences(() => {
    const next = visibleUnreadThreadCount();
    if (next !== unreadThreadsCount) {
      unreadThreadsCount = next;
      emit();
    }
  });
  // Membership changes (added to a group, leave, delete) move the unread pile.
  subscribeUserThreadEvents((detail) => {
    if (!String(detail?.action || '').startsWith('task')) scheduleWsRefresh(WS_UNKNOWN_DEBOUNCE_MS);
  });
  // Every chat message lands here; internal ones move counts. update:true is an in-place
  // edit (reaction, soft-delete notice) and never changes unread.
  subscribeChatMessages((detail) => {
    if (detail?.kind !== 'message' || detail?.update) return;
    if (!chatsEligible()) return;
    const tid = String(detail?.message?.threadId || '').trim();
    if (!tid) return;
    if (internalThreadIds.has(tid)) {
      scheduleWsRefresh(WS_KNOWN_DEBOUNCE_MS);
    } else if (directThreadIds.has(tid)) {
      // A private contact chat has its own pane and its own badge; it must not move the Chats
      // count, and it must not be mistaken for an unknown internal thread worth refetching.
      void refreshPrivateUnread();
      return;
    } else if (!looksLikePatientChannelMessage(detail.message)) {
      scheduleWsRefresh(WS_UNKNOWN_DEBOUNCE_MS);
    }
  });
}

/** Number of internal chats with unread messages (0 until the first fetch). */
export function getChatsUnreadThreadsCount() {
  ensureStarted();
  return unreadThreadsCount;
}

/** threadId -> unread count map (read-only; >0 entries only). */
export function getChatsUnreadByThreadId() {
  ensureStarted();
  return byThreadId;
}

export function subscribeChatsUnread(listener) {
  if (typeof listener !== 'function') return () => {};
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True when the thread is a known PRIVATE contact chat (see directThreadIds). */
export function isKnownDirectThreadId(threadId) {
  ensureStarted();
  return directThreadIds.has(String(threadId || '').trim());
}

/** Feed private-chat thread ids discovered elsewhere (the Private pane's loaded list). */
export function noteDirectThreadIds(ids) {
  ensureStarted();
  for (const raw of Array.isArray(ids) ? ids : []) {
    const id = String(raw || '').trim();
    if (id) directThreadIds.add(id);
  }
}

/**
 * Whether the thread is a KNOWN internal chat this session. Used by the notification
 * layer to route clicks to Chats and to apply chat notification rules. False only means
 * "not seen yet" — callers with a threadId miss should fall back to fetching the thread.
 */
export function isKnownInternalThreadId(threadId) {
  ensureStarted();
  return internalThreadIds.has(String(threadId || '').trim());
}

/**
 * Feed internal thread ids discovered elsewhere (the Chats pane's loaded list), so scope
 * routing works even before the first summary response.
 */
export function noteInternalThreadIds(ids) {
  ensureStarted();
  for (const raw of Array.isArray(ids) ? ids : []) {
    const id = String(raw || '').trim();
    if (id) internalThreadIds.add(id);
  }
}

/** Optimistically zero a thread's unread count (thread opened / marked read). */
export function markThreadReadLocally(threadId) {
  const tid = String(threadId || '').trim();
  if (!tid || !(tid in byThreadId)) return;
  const next = { ...byThreadId };
  delete next[tid];
  setCounts(next);
}

/** Optimistically flag a thread unread (context-menu "Mark as unread"). */
export function markThreadUnreadLocally(threadId) {
  const tid = String(threadId || '').trim();
  if (!tid) return;
  internalThreadIds.add(tid);
  if (byThreadId[tid] > 0) return;
  setCounts({ ...byThreadId, [tid]: 1 });
}
